import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { api } from '../lib/api';
import type { Appointment } from '../lib/types';

export function SuccessPage() {
  const { id = '' } = useParams();
  const routeState = useLocation().state as { eventId?: string; eventUrl?: string | null } | null;
  const appointment = useQuery({
    queryKey: ['appointment', id],
    queryFn: () => api<{ data: Appointment }>(`/api/appointments/${id}`).then((response) => response.data),
  });
  const queryClient = useQueryClient();
  const pendingMeetingItems = (appointment.data?.groupItems?.length
    ? appointment.data.groupItems
    : appointment.data ? [appointment.data] : [])
    .filter((item) => item.templateType === 'meeting' && item.googleEventId
      && item.meetingSync.status !== 'synced');
  const retryMeetingSync = useMutation({
    mutationFn: async () => {
      for (const item of pendingMeetingItems) {
        await api(`/api/appointments/${item.id}/meeting-sync`, { method: 'POST' });
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['appointment', id] }),
  });
  const eventId = routeState?.eventId ?? appointment.data?.googleEventId;
  const eventUrl = routeState?.eventUrl ?? appointment.data?.googleEventUrl;
  return (
    <Layout title="บันทึกสำเร็จ">
      <section className="mx-auto max-w-lg text-center">
        <div className="card py-9">
          <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-emerald-100 text-4xl text-emerald-700">✓</div>
          <h2 className="mt-5 text-2xl font-bold">เพิ่มนัดหมายแล้ว</h2>
          <p className="mt-2 text-slate-600">กิจกรรมถูกสร้างใน Google Calendar เรียบร้อย</p>
          {eventId && <p className="mt-2 text-xs text-slate-400">Event ID: {eventId}</p>}
        </div>
        {pendingMeetingItems.length > 0 && (
          <div className="mt-4 rounded-2xl border border-amber-300 bg-amber-50 p-4 text-left text-amber-950">
            <p className="font-bold">รอสร้างการประชุม {pendingMeetingItems.length} รายการ</p>
            <p className="mt-1 text-sm">กิจกรรม Calendar สำเร็จแล้ว แต่ยังเพิ่มเข้าระบบการประชุมไม่ครบ</p>
            <button
              type="button"
              className="btn-secondary mt-3 w-full"
              onClick={() => retryMeetingSync.mutate()}
              disabled={retryMeetingSync.isPending}
            >
              {retryMeetingSync.isPending ? 'กำลังลองอีกครั้ง…' : 'ลองสร้างการประชุมอีกครั้ง'}
            </button>
          </div>
        )}
        <div className="mt-5 grid gap-3">
          {eventUrl && <a href={eventUrl} target="_blank" rel="noreferrer" className="btn-primary">เปิดใน Google Calendar</a>}
          <Link to={`/appointments/${id}`} className="btn-secondary">ดูรายละเอียดใบนัด</Link>
          {appointment.data?.templateType === 'meeting' && <Link to="/meetings" className="btn-secondary">เปิดการประชุม</Link>}
          <Link to="/create" className="btn-secondary">สร้างรายการใหม่</Link>
        </div>
      </section>
    </Layout>
  );
}
